import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SaveIcon from "@mui/icons-material/Save";

const newFineRow = () => ({
  key: `${Date.now()}-${Math.random()}`,
  from_date: "",
  fine_amount: "",
});

const seatTypes = [
  { value: "ALL", label: "All" },
  { value: "C", label: "Convener" },
  { value: "M", label: "Management/Spot" },
];

const getRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  return payload?.items || payload?.data?.items || payload?.data || [];
};

const toDateInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const isWithinActiveDates = (head, refDate) => {
  const date = refDate ? new Date(refDate) : new Date();
  date.setHours(0, 0, 0, 0);

  // If fee head has a start_date, enforce it only when the fee head
  // was created on/after the reference date. If it was created before
  // the reference date, ignore the start_date (allow immediate use).
  if (head.start_date) {
    const createdOn = head.created_on ? new Date(head.created_on) : null;
    if (
      !(
        createdOn &&
        (() => {
          createdOn.setHours(0, 0, 0, 0);
          return createdOn.getTime() <= date.getTime();
        })()
      )
    ) {
      const startDate = new Date(head.start_date);
      startDate.setHours(0, 0, 0, 0);
      if (date < startDate) return false;
    }
  }
  if (head.end_date) {
    const endDate = new Date(head.end_date);
    endDate.setHours(23, 59, 59, 999);
    if (date > endDate) return false;
  }
  return true;
};

const matchesText = (value, search) =>
  !search ||
  String(value || "")
    .toUpperCase()
    .includes(search.toUpperCase());

export default function FeeFineDates() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const user = useMemo(() => JSON.parse(sessionStorage.getItem("user") || "{}"), []);
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const headers = { Authorization: `Bearer ${sessionStorage.getItem("token")}` };

  const [years, setYears] = useState([]);
  const [batches, setBatches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [feeHeads, setFeeHeads] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState({ open: false, text: "", severity: "success" });
  const [form, setForm] = useState({
    acyear_id: "",
    fee_category_id: "",
    fee_head_ids: [],
    admittedbatch_ids: [],
    seat_type: "ALL",
    percent: "100",
  });
  const [fineRows, setFineRows] = useState([newFineRow()]);

  const notify = (text, severity = "success") => setMessage({ open: true, text, severity });

  const loadRules = async () => {
    const { data } = await axios.get(
      `${API_BASE_URL}/api/fee-fine-dates/${clientId}/${collegeId}`,
      {
        headers,
      }
    );
    setRules(getRows(data).map((row) => ({ ...row, id: String(row.fee_fine_date_id) })));
  };

  useEffect(() => {
    if (!clientId || !collegeId) return;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${API_BASE_URL}/api/fee-fine-dates/${clientId}/${collegeId}/options`,
          { headers }
        );
        const options = data?.data || {};
        const yearRows = options.years || [];
        setYears(yearRows);
        setBatches(options.batches || []);
        setCategories(options.categories || []);
        setTypes(options.types || []);
        setFeeHeads(options.feeHeads || []);
        if (yearRows.length) {
          const activeYear = yearRows.find((year) => Number(year.is_active) === 1) || yearRows[0];
          setForm((prev) => ({
            ...prev,
            acyear_id: prev.acyear_id || String(activeYear.acyear_id),
          }));
        }
        await loadRules();
      } catch (error) {
        notify(error.response?.data?.message || "Unable to load late fine setup", "error");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, collegeId]);

  const selectedYear = useMemo(
    () => years.find((item) => String(item.acyear_id) === String(form.acyear_id)),
    [form.acyear_id, years]
  );

  const selectedCategoryFeeTypeIds = useMemo(
    () =>
      types
        .filter((type) => String(type.fee_category_id) === String(form.fee_category_id))
        .map((type) => String(type.fee_type_id)),
    [form.fee_category_id, types]
  );

  const filteredFeeHeads = useMemo(() => {
    const refDate = fineRows && fineRows[0] ? fineRows[0].from_date : undefined;
    return feeHeads.filter((head) => {
      const collegeOk = !collegeId || String(head.college_id) === String(collegeId);
      const categoryOk =
        !form.fee_category_id ||
        String(head.feeType?.fee_category_id) === String(form.fee_category_id) ||
        selectedCategoryFeeTypeIds.includes(String(head.fee_type_id));
      const yearOk =
        !selectedYear?.academic_year || head.academic_year === selectedYear.academic_year;
      return (
        collegeOk &&
        categoryOk &&
        yearOk &&
        isWithinActiveDates(head, refDate) &&
        matchesText(head.fee_head_name, search)
      );
    });
  }, [
    collegeId,
    feeHeads,
    form.fee_category_id,
    search,
    selectedCategoryFeeTypeIds,
    selectedYear,
    fineRows,
  ]);

  const sortedRules = useMemo(
    () =>
      [...rules].sort((a, b) => {
        const statusDiff = Number(a.status || 0) - Number(b.status || 0);
        if (statusDiff !== 0) return statusDiff;
        const dateDiff = new Date(a.from_date || 0) - new Date(b.from_date || 0);
        if (dateDiff !== 0) return dateDiff;
        return Number(b.fee_fine_date_id || 0) - Number(a.fee_fine_date_id || 0);
      }),
    [rules]
  );

  const setField = (field, value) =>
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "fee_category_id" ? { fee_head_ids: [] } : {}),
    }));

  const setMulti = (field, event) =>
    setField(
      field,
      Array.from(event.target.selectedOptions || []).map((option) => option.value)
    );

  const setFineRow = (key, field, value) =>
    setFineRows((rows) => rows.map((row) => (row.key === key ? { ...row, [field]: value } : row)));

  const addFineRow = () => setFineRows((rows) => [...rows, newFineRow()]);

  const reset = () => {
    setEditingId("");
    setForm((prev) => ({
      ...prev,
      fee_head_ids: [],
      admittedbatch_ids: [],
      seat_type: "ALL",
      percent: "100",
    }));
    setFineRows([newFineRow()]);
  };

  const validate = () => {
    if (!form.acyear_id) return "Select academic year";
    if (!form.fee_category_id) return "Select fee category";
    if (!form.fee_head_ids.length) return "Select at least one fee head";
    if (!form.admittedbatch_ids.length) return "Select at least one admitted batch";
    if (!form.percent || Number(form.percent) <= 0 || Number(form.percent) > 100) {
      return "Below fee percent must be between 1 and 100";
    }
    if (
      fineRows.some((row) => !row.from_date || !row.fine_amount || Number(row.fine_amount) <= 0)
    ) {
      return "Complete every fine date and amount";
    }
    return "";
  };

  const save = async () => {
    const errorText = validate();
    if (errorText) return notify(errorText, "warning");
    const payload = {
      ...form,
      created_by: user?.user_email || "system",
      modified_by: user?.user_email || "system",
      dates: fineRows.map((row) => ({
        from_date: row.from_date,
        fine_amount: row.fine_amount,
      })),
    };
    setLoading(true);
    try {
      if (editingId) {
        await axios.put(
          `${API_BASE_URL}/api/fee-fine-dates/${clientId}/${collegeId}/${editingId}`,
          {
            ...payload,
            from_date: fineRows[0].from_date,
            fine_amount: fineRows[0].fine_amount,
          },
          { headers }
        );
        const newRows = fineRows.slice(1);
        if (newRows.length) {
          await axios.post(
            `${API_BASE_URL}/api/fee-fine-dates/${clientId}/${collegeId}`,
            {
              ...payload,
              dates: newRows.map((row) => ({
                from_date: row.from_date,
                fine_amount: row.fine_amount,
              })),
            },
            { headers }
          );
        }
      } else {
        await axios.post(`${API_BASE_URL}/api/fee-fine-dates/${clientId}/${collegeId}`, payload, {
          headers,
        });
      }
      notify(editingId ? "Late fine date rule updated" : "Late fine date rules saved");
      reset();
      await loadRules();
    } catch (error) {
      notify(error.response?.data?.message || "Unable to save late fine date rules", "error");
    } finally {
      setLoading(false);
    }
  };

  const edit = (row) => {
    if (Number(row.status) !== 0) return notify("Applied rules cannot be edited", "warning");
    setEditingId(String(row.fee_fine_date_id));
    setForm({
      acyear_id: String(row.acyear_id || ""),
      fee_category_id: String(row.fee_category_id || ""),
      fee_head_ids: String(row.fee_head_ids || "")
        .split(",")
        .filter(Boolean),
      admittedbatch_ids: String(row.admittedbatch_ids || "")
        .split(",")
        .filter(Boolean),
      seat_type: row.seat_type || "ALL",
      percent: String(Number(row.percent || 100)),
    });
    setFineRows([
      {
        key: String(row.fee_fine_date_id),
        from_date: toDateInput(row.from_date),
        fine_amount: String(Number(row.fine_amount || 0)),
      },
    ]);
  };

  const remove = async (row) => {
    if (Number(row.status) !== 0) return notify("Applied rules cannot be deleted", "warning");
    const result = await Swal.fire({
      title: "Delete this late fine rule?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/api/fee-fine-dates/${clientId}/${collegeId}/${row.fee_fine_date_id}`,
        {
          headers,
          data: { modified_by: user?.user_email || "system" },
        }
      );
      notify("Late fine date rule deleted");
      await loadRules();
    } catch (error) {
      notify(error.response?.data?.message || "Unable to delete rule", "error");
    }
  };

  const applyDue = async () => {
    const result = await Swal.fire({
      title: "Apply due late fines now?",
      text: "This runs the same action as the late fine cron job for this college.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Apply",
    });
    if (!result.isConfirmed) return;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/fee-fine-dates/${clientId}/${collegeId}/apply-due`,
        {},
        { headers }
      );
      notify(`Processed ${data?.data?.processed_rules || 0} due rule(s)`);
      await loadRules();
    } catch (error) {
      notify(error.response?.data?.message || "Unable to apply due late fines", "error");
    } finally {
      setLoading(false);
    }
  };

  const wrapCell = (value) => (
    <Typography
      variant="caption"
      sx={{
        whiteSpace: "normal",
        wordBreak: "break-word",
        lineHeight: 1.35,
        py: 1,
      }}
    >
      {value || ""}
    </Typography>
  );

  const columns = [
    { field: "fee_fine_date_id", headerName: "SNo", width: 90 },
    {
      field: "fee_head_names",
      headerName: "Fee Head",
      minWidth: 300,
      flex: 1.2,
      renderCell: (params) => wrapCell(params.value),
    },
    {
      field: "admitted_batches",
      headerName: "Admitted Batch",
      minWidth: 180,
      flex: 0.8,
      renderCell: (params) => wrapCell(params.value),
    },
    {
      field: "from_date",
      headerName: "Fine Date",
      width: 130,
      valueGetter: (params) => toDateInput(params.row.from_date),
    },
    {
      field: "fine_amount",
      headerName: "Fine",
      width: 110,
      valueGetter: (params) => Number(params.row.fine_amount || 0),
    },
    {
      field: "status_label",
      headerName: "Status",
      width: 130,
      valueGetter: (params) => (Number(params.row.status) === 0 ? "To be applied" : "Applied"),
    },
    {
      field: "actions",
      headerName: "Action",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row">
          <Tooltip title="Edit">
            <span>
              <IconButton
                disabled={Number(params.row.status) !== 0}
                onClick={() => edit(params.row)}
              >
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete">
            <span>
              <IconButton
                color="error"
                disabled={Number(params.row.status) !== 0}
                onClick={() => remove(params.row)}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Typography variant="h5">Assign Late Fee Fine Dates</Typography>
        <Button
          startIcon={<PlayArrowIcon />}
          variant="contained"
          disabled={loading}
          onClick={applyDue}
          sx={{ bgcolor: "#1976d2", color: "#fff !important", "&:hover": { bgcolor: "#125aa3" } }}
        >
          Apply Due Fines
        </Button>
      </Stack>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              size="medium"
              label="Academic Year"
              value={form.acyear_id}
              onChange={(e) => setField("acyear_id", e.target.value)}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
            >
              <option value="">Select Academic Year</option>
              {years.map((year) => (
                <option key={year.acyear_id} value={year.acyear_id}>
                  {year.academic_year}
                </option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              size="medium"
              label="Fee Category"
              value={form.fee_category_id}
              onChange={(e) => setField("fee_category_id", e.target.value)}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
            >
              <option value="">Select Fee Category</option>
              {categories.map((category) => (
                <option key={category.fee_category_id} value={category.fee_category_id}>
                  {category.fee_category_name}
                </option>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="medium"
              label="Search Fee Head"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Type keywords, e.g., 2024-2025 TUTION"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Fee Head"
              value={form.fee_head_ids}
              onChange={(e) => setMulti("fee_head_ids", e)}
              SelectProps={{ native: true, multiple: true }}
              inputProps={{ size: 9 }}
              InputLabelProps={{ shrink: true }}
            >
              {filteredFeeHeads.map((head) => (
                <option key={head.fee_head_id} value={String(head.fee_head_id)}>
                  {head.fee_head_name}
                </option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Admitted Batch"
              value={form.admittedbatch_ids}
              onChange={(e) => setMulti("admittedbatch_ids", e)}
              SelectProps={{ native: true, multiple: true }}
              inputProps={{ size: 8 }}
              InputLabelProps={{ shrink: true }}
            >
              {batches.map((batch) => (
                <option key={batch.admittedbatch_id} value={String(batch.admittedbatch_id)}>
                  {batch.admittedbatch}
                </option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              size="medium"
              label="Seat Type"
              value={form.seat_type}
              onChange={(e) => setField("seat_type", e.target.value)}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
            >
              {seatTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="medium"
              type="number"
              label="Below Fee Percent"
              value={form.percent}
              inputProps={{ min: 1, max: 100 }}
              onChange={(e) => setField("percent", e.target.value)}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Add Dates & Fines
          </Typography>
          {fineRows.map((row, index) => (
            <Grid container spacing={1.5} alignItems="center" key={row.key} sx={{ mb: 1 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="medium"
                  type="date"
                  label="Fine Date"
                  value={row.from_date}
                  onChange={(e) => setFineRow(row.key, "from_date", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="medium"
                  type="number"
                  label="Fine Amount"
                  value={row.fine_amount}
                  onChange={(e) => setFineRow(row.key, "fine_amount", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Tooltip title="Delete row">
                    <span>
                      <IconButton
                        color="error"
                        disabled={fineRows.length === 1}
                        onClick={() =>
                          setFineRows((rows) => rows.filter((item) => item.key !== row.key))
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {index === fineRows.length - 1 && (
                    <Button
                      startIcon={<AddIcon />}
                      variant="contained"
                      size="medium"
                      onClick={addFineRow}
                      sx={{
                        bgcolor: "#1976d2",
                        color: "#fff !important",
                        "&:hover": { bgcolor: "#125aa3" },
                      }}
                    >
                      Add Date & Fine
                    </Button>
                  )}
                </Stack>
              </Grid>
            </Grid>
          ))}
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              startIcon={<SaveIcon />}
              variant="contained"
              disabled={loading}
              onClick={save}
              sx={{
                bgcolor: "#1976d2",
                color: "#fff !important",
                "&:hover": { bgcolor: "#125aa3" },
              }}
            >
              {editingId ? "Update Dates & Fine" : "Save Dates & Fine"}
            </Button>
            {editingId && <Button onClick={reset}>Cancel</Button>}
          </Stack>
        </Box>
      </Paper>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Late Fee Dates & Fines Management
      </Typography>
      <Paper variant="outlined" sx={{ height: 440 }}>
        <DataGrid
          rows={sortedRules}
          columns={columns}
          loading={loading}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          getRowHeight={() => "auto"}
          sx={{
            "& .MuiDataGrid-cell": {
              alignItems: "flex-start",
              py: 1,
            },
          }}
        />
      </Paper>
      <Snackbar
        open={message.open}
        autoHideDuration={5000}
        onClose={() => setMessage((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={message.severity}>{message.text}</Alert>
      </Snackbar>
    </Box>
  );
}
